import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { BarChart3, Calendar, Download, Wallet } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const BASE = process.env.REACT_APP_BACKEND_URL;

function downloadCSV(path, filename) {
  const token = localStorage.getItem("lpg_token");
  return fetch(`${BASE}/api${path}`, {
    method: "GET", credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then(async (r) => {
    if (!r.ok) throw new Error(r.statusText);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
}

export default function AttendanceSummary() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/attendance/summary?year=${year}&month=${month}`);
      setData(d);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year, month]);

  const yearOptions = [];
  for (let y = today.getFullYear() - 3; y <= today.getFullYear() + 1; y++) yearOptions.push(y);

  return (
    <div className="space-y-6" data-testid="attendance-summary-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <BarChart3 size={26} strokeWidth={2.5} />
          <div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Monthly Summary</h1>
            <p className="text-xs text-gray-500">Auto-calculated payable amount per employee</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/attendance" className="h-10 px-4 border border-gray-300 rounded-sm font-semibold text-sm inline-flex items-center gap-2 hover:bg-gray-50" data-testid="goto-attendance">
            <Calendar size={16} /> Mark Attendance
          </Link>
          <button
            onClick={() => downloadCSV(`/exports/attendance-summary?year=${year}&month=${month}`, `attendance_summary_${year}_${String(month).padStart(2, "0")}.csv`)}
            className="h-10 px-4 bg-black text-white rounded-sm font-semibold text-sm inline-flex items-center gap-2"
            data-testid="download-summary-csv"
          >
            <Download size={16} /> CSV
          </button>
        </div>
      </div>

      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="small-label block mb-1">Month</label>
          <select className="input-lg" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="month-select">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="small-label block mb-1">Year</label>
          <select className="input-lg" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="year-select">
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="card-flat p-3 flex items-center gap-3">
          <Wallet size={22} className="text-gray-500" />
          <div>
            <div className="small-label text-gray-500">Total Payable</div>
            <div className="data-num text-2xl text-[#0A0A0A]" data-testid="total-payable">
              ₹ {formatMoney(data?.totals?.payable || 0)}
            </div>
          </div>
        </div>
      </div>

      <div className="card-flat">
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Employee</th>
                <th className="text-left">Role</th>
                <th className="text-left">Wage</th>
                <th className="num">Present</th>
                <th className="num">Half</th>
                <th className="num">Absent</th>
                <th className="num">Eff. Days</th>
                <th className="num">Payable</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="py-6 text-center text-gray-400">Loading…</td></tr>}
              {!loading && (data?.rows || []).length === 0 && (
                <tr><td colSpan="8" className="py-10 text-center text-gray-400 text-sm">
                  No employees. Add some from <Link className="underline font-semibold" to="/employees">Employees</Link>.
                </td></tr>
              )}
              {(data?.rows || []).map((r) => (
                <tr key={r.employee_id} data-testid={`summary-row-${r.employee_id}`}>
                  <td className="font-semibold">{r.name}</td>
                  <td className="text-gray-600">{r.role || "—"}</td>
                  <td className="text-xs">
                    {r.wage_type === "monthly"
                      ? `₹ ${formatMoney(r.monthly_salary)} /mo`
                      : `₹ ${formatMoney(r.daily_wage)} /day`}
                  </td>
                  <td className="num text-[#15803D] font-bold">{r.present}</td>
                  <td className="num text-[#B45309] font-bold">{r.half_day}</td>
                  <td className="num text-[#B91C1C] font-bold">{r.absent}</td>
                  <td className="num data-num">{r.effective_days}</td>
                  <td className="num data-num font-bold">₹ {formatMoney(r.payable)}</td>
                </tr>
              ))}
            </tbody>
            {!loading && (data?.rows || []).length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-300">
                  <td colSpan="3" className="font-bold uppercase text-xs tracking-wider">Total</td>
                  <td className="num text-[#15803D] font-bold">{data.totals.present}</td>
                  <td className="num text-[#B45309] font-bold">{data.totals.half_day}</td>
                  <td className="num text-[#B91C1C] font-bold">{data.totals.absent}</td>
                  <td></td>
                  <td className="num data-num font-black text-base">₹ {formatMoney(data.totals.payable)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {data && (
        <div className="text-xs text-gray-500">
          {MONTHS[month - 1]} {year} has {data.days_in_month} days · Monthly salary divided by total days × effective days.
        </div>
      )}
    </div>
  );
}
